"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { track } from "../lib/track";
import { isNavRouteActive } from "../lib/navActive";

type Route = {
  name: string;
  link: string;
  matchPaths?: string[];
  excludePaths?: string[];
  secondaryLinks?: { name: string; link: string }[];
};

type DesktopMenuProps = { routes: Route[] };

const OPEN_DELAY  = 60;
const CLOSE_DELAY = 120;

const DesktopMenu = ({ routes }: DesktopMenuProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  // Defer pathname to client-only to avoid hydration mismatch
  const [activePath, setActivePath] = useState<string>("");
  const timers   = useRef<{ open?: ReturnType<typeof setTimeout>; close?: ReturnType<typeof setTimeout> }>({});
  const pathname = usePathname();

  // Sync after mount only — server always gets ""
  useEffect(() => {
    setActivePath(pathname ?? "");
  }, [pathname]);

  const open = (i: number) => {
    clearTimeout(timers.current.close);
    timers.current.open = setTimeout(() => setOpenIndex(i), OPEN_DELAY);
  };

  const close = () => {
    clearTimeout(timers.current.open);
    timers.current.close = setTimeout(() => setOpenIndex(null), CLOSE_DELAY);
  };

  const isRouteActive = (route: Route) =>
    isNavRouteActive(activePath, route.link, route.matchPaths, route.excludePaths);

  return (
    <div className="flex items-center gap-1">
      <ul className="flex items-center space-x-1">
        {routes.map((route, i) => {
          const isOpen   = openIndex === i && !!route.secondaryLinks?.length;
          const isActive = isRouteActive(route);

          return (
            <li
              key={route.name}
              className="relative"
              onMouseEnter={() => open(i)}
              onMouseLeave={close}
              onFocus={() => open(i)}
              onBlur={close}
            >
              <div className="flex items-center">
                <Link
                  href={route.link}
                  prefetch={false}
                  aria-haspopup={route.secondaryLinks ? "menu" : undefined}
                  aria-expanded={route.secondaryLinks ? isOpen : undefined}
                  className={`
                    px-3 py-2 rounded-lg text-sm tracking-wide transition-colors
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]
                    ${isActive
                      ? "text-[var(--text-on-dark)] bg-white/10"
                      : "text-[var(--text-on-dark-muted)] hover:text-[var(--text-on-dark)] hover:bg-white/8"
                    }
                  `}
                  onClick={() =>
                    track("nav_click", {
                      label: route.name,
                      component: "DesktopMenu",
                    }, { event: "nav_click", label: route.name, component: "DesktopMenu", meta: { routePath: route.link, timestamp: Date.now() } })
                  }
                >
                  {route.name}
                </Link>
                {!!route.secondaryLinks?.length && (
                  <span
                    className={`ml-1 text-xs select-none pointer-events-none transition-colors ${
                      isActive ? "text-[var(--text-on-dark)]" : "text-[var(--text-on-dark-muted)]"
                    }`}
                    aria-hidden="true"
                  >
                    ▼
                  </span>
                )}
              </div>

              {/* Dropdown */}
              {!!route.secondaryLinks?.length && (
                <ul
                  role="menu"
                  aria-hidden={!isOpen}
                  className={[
                    "absolute left-0 mt-3 min-w-[220px] rounded-2xl",
                    "bg-[var(--bg-surface-dk)]",
                    "shadow-xl shadow-black/20 ring-1 ring-[var(--border-on-light)]",
                    "p-2.5 z-50",
                    "transition-all duration-150 ease-out",
                    isOpen
                      ? "opacity-100 translate-y-0 visible"
                      : "opacity-0 -translate-y-1 invisible pointer-events-none",
                    "before:content-[''] before:absolute before:-top-3 before:left-0 before:h-3 before:w-full",
                  ].join(" ")}
                >
                  {route.secondaryLinks.map((secondary) => {
                    const isSubActive = activePath !== "" && activePath === secondary.link;
                    return (
                      <li key={secondary.name} role="none">
                        <Link
                          role="menuitem"
                          href={secondary.link}
                          prefetch={false}
                          tabIndex={isOpen ? 0 : -1}
                          className={`
                            block px-4 py-3 rounded-xl text-[15px] whitespace-nowrap leading-none
                            transition-colors
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]
                            ${isSubActive
                              ? "bg-[var(--bg-card)] text-[var(--text-brand)] font-medium"
                              : "text-[var(--text-on-light-muted)] hover:bg-[var(--bg-card)] hover:text-[var(--text-on-light)]"
                            }
                          `}
                          onClick={() =>
                            track("nav_click", {
                              label: `${route.name} > ${secondary.name}`,
                              component: "DesktopMenu",
                            }, {
                              event: "nav_click",
                              label: `${route.name} > ${secondary.name}`,
                              component: "DesktopMenu",
                              meta: { routePath: route.link, secondaryName: secondary.name, timestamp: Date.now() },
                            })
                          }
                        >
                          {secondary.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {/* CTA */}
      <Link
        href="/workwithme/discoverycall"
        prefetch={false}
        className="
          ml-4 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap
          bg-[var(--btn-inverse-bg)] text-[var(--btn-inverse-text)]
          hover:bg-[var(--btn-inverse-hover)] transition-colors
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]
        "
        onClick={() =>
          track("nav_click", {
            label: "CTA: Free Discovery Call",
            component: "DesktopMenu",
          }, { event: "nav_click", label: "CTA: Free Discovery Call", component: "DesktopMenu", meta: { routePath: "/workwithme/discoverycall", timestamp: Date.now() } })
        }
      >
        Free discovery call
      </Link>
    </div>
  );
};

export default DesktopMenu;
